import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  Checkbox,
  Menu,
  MenuItem,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Plus as PlusIcon,
  RefreshCw,
  Search,
  FolderKanban,
  Columns3
} from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '../components/ui/button';
import api from '../services/api';

export default function TipiAllegatoUO() {
  const columnDefs = [
    { key: 'codice', label: 'Codice', sortable: true, align: 'left' },
    { key: 'nome', label: 'Nome', sortable: true, align: 'left' },
    { key: 'descrizione', label: 'Descrizione', sortable: false, align: 'left' },
    { key: 'ordine', label: 'Ordine', sortable: true, align: 'right' },
    { key: 'attivo', label: 'Attivo', sortable: true, align: 'center' },
    { key: 'allegati_totale', label: 'Totale Allegati', sortable: true, align: 'right' },
    { key: 'allegati_percentuale', label: '% sul totale', sortable: true, align: 'right' }
  ];

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('ordine');
  const [filterText, setFilterText] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState([
    'nome',
    'descrizione',
    'ordine',
    'attivo',
    'allegati_totale',
    'allegati_percentuale'
  ]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const [formData, setFormData] = useState({
    codice: '',
    nome: '',
    descrizione: '',
    ordine: 0,
    attivo: true
  });

  useEffect(() => {
    fetchRows();
  }, []);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const response = await api.get('/tipi-allegato-uo');
      setRows(Array.isArray(response.data) ? response.data : []);
      setError('');
    } catch (err) {
      console.error('Error fetching tipi allegato U/O:', err);
      setError('Errore nel caricamento dei tipi allegato U/O');
      toast.error('Errore nel caricamento dei tipi allegato U/O');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (row = null) => {
    if (row) {
      setCurrentRow(row);
      setFormData({
        codice: row.codice || '',
        nome: row.nome || '',
        descrizione: row.descrizione || '',
        ordine: Number(row.ordine || 0),
        attivo: Boolean(row.attivo)
      });
    } else {
      setCurrentRow(null);
      setFormData({
        codice: '',
        nome: '',
        descrizione: '',
        ordine: 0,
        attivo: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentRow(null);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        codice: String(formData.codice || '').trim().toUpperCase(),
        nome: String(formData.nome || '').trim(),
        descrizione: String(formData.descrizione || '').trim() || null,
        ordine: Number(formData.ordine || 0),
        attivo: Boolean(formData.attivo)
      };
      if (!payload.codice || !payload.nome) {
        toast.error('Codice e nome sono obbligatori');
        return;
      }

      if (currentRow?.id) {
        await api.put(`/tipi-allegato-uo/${currentRow.id}`, payload);
        toast.success('Tipo allegato aggiornato');
      } else {
        await api.post('/tipi-allegato-uo', payload);
        toast.success('Tipo allegato creato');
      }
      handleCloseDialog();
      fetchRows();
    } catch (err) {
      console.error('Error saving tipo allegato U/O:', err);
      toast.error(err?.response?.data?.message || 'Errore nel salvataggio');
    }
  };

  const openDeleteDialog = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/tipi-allegato-uo/${deleteTarget.id}`);
      toast.success('Tipo allegato eliminato');
      closeDeleteDialog();
      fetchRows();
    } catch (err) {
      console.error('Error deleting tipo allegato U/O:', err);
      toast.error(err?.response?.data?.message || "Errore durante l'eliminazione");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const displayedColumns = columnDefs.filter((col) => visibleColumns.includes(col.key));

  const handleToggleColumn = (columnKey) => {
    setVisibleColumns((prev) => {
      const isVisible = prev.includes(columnKey);
      if (isVisible && prev.length === 1) return prev;
      if (isVisible) return prev.filter((key) => key !== columnKey);
      return [...prev, columnKey];
    });
  };

  const filteredRows = rows
    .filter((row) => {
      if (!filterText) return true;
      const search = filterText.toLowerCase();
      return (
        (row.codice || '').toLowerCase().includes(search) ||
        (row.nome || '').toLowerCase().includes(search) ||
        (row.descrizione || '').toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      const isAsc = order === 'asc';
      const valA = a[orderBy];
      const valB = b[orderBy];
      if (
        orderBy === 'attivo' ||
        orderBy === 'id' ||
        orderBy === 'ordine' ||
        orderBy === 'allegati_totale' ||
        orderBy === 'allegati_percentuale'
      ) {
        const numA = Number(valA || 0);
        const numB = Number(valB || 0);
        return isAsc ? numA - numB : numB - numA;
      }
      const strA = (valA || '').toString().toLowerCase();
      const strB = (valB || '').toString().toLowerCase();
      if (strA < strB) return isAsc ? -1 : 1;
      if (strA > strB) return isAsc ? 1 : -1;
      return 0;
    });

  return (
    <Box className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-50">
            <FolderKanban className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <Typography variant="h4" className="font-bold text-gray-800 dark:text-gray-100">
              Tipi Allegato U/O
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Categorie documentali per gli allegati delle pratiche Urgenze/Opposizioni
            </Typography>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchRows} title="Ricarica">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={() => handleOpenDialog()}>
            <PlusIcon className="mr-2 h-4 w-4" />
            Nuovo Tipo Allegato
          </Button>
        </div>
      </div>

      {error && (
        <Alert severity="error" className="mb-4">
          {error}
        </Alert>
      )}

      <Menu
        anchorEl={columnsMenuAnchor}
        open={Boolean(columnsMenuAnchor)}
        onClose={() => setColumnsMenuAnchor(null)}
      >
        {columnDefs.map((column) => {
          const checked = visibleColumns.includes(column.key);
          const disableHide = checked && visibleColumns.length === 1;
          return (
            <MenuItem
              key={column.key}
              dense
              onClick={() => handleToggleColumn(column.key)}
              disabled={disableHide}
            >
              <Checkbox size="small" checked={checked} />
              <ListItemText primary={column.label} />
            </MenuItem>
          );
        })}
      </Menu>

      <Card className="shadow-sm border-none overflow-hidden rounded-xl">
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            value={filterText}
            onChange={(event) => {
              setFilterText(event.target.value);
              setPage(0);
            }}
            placeholder="Cerca per codice, nome o descrizione..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              )
            }}
            sx={{ maxWidth: 520 }}
          />
          <Tooltip title="Colonne">
            <IconButton onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}>
              <Columns3 size={18} />
            </IconButton>
          </Tooltip>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {displayedColumns.map((column) => (
                  <TableCell key={column.key} align={column.align}>
                    {column.sortable ? (
                      <TableSortLabel
                        active={orderBy === column.key}
                        direction={orderBy === column.key ? order : 'asc'}
                        onClick={() => handleRequestSort(column.key)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={displayedColumns.length + 1} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={displayedColumns.length + 1} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nessun tipo allegato trovato
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow key={row.id} hover>
                      {displayedColumns.map((column) => (
                        <TableCell key={`${row.id}-${column.key}`} align={column.align} sx={column.key === 'nome' ? { fontWeight: 600 } : undefined}>
                          {column.key === 'attivo' ? (
                            <Checkbox checked={Boolean(row.attivo)} disabled />
                          ) : column.key === 'ordine' ? (
                            Number(row.ordine || 0)
                          ) : column.key === 'allegati_totale' ? (
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 1,
                                border: '1px solid rgba(245, 158, 11, 0.35)',
                                backgroundColor: 'rgba(245, 158, 11, 0.04)',
                                minWidth: 56
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#F59E0B', letterSpacing: 0.2 }}>
                                {Number(row.allegati_totale) || 0}
                              </Typography>
                            </Box>
                          ) : column.key === 'allegati_percentuale' ? (
                            (() => {
                              const rawPercent = Number(row.allegati_percentuale);
                              const safePercent = Number.isFinite(rawPercent) ? rawPercent : 0;
                              const clampedPercent = Math.max(0, Math.min(100, safePercent));
                              return (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                    <CircularProgress
                                      variant="determinate"
                                      value={100}
                                      size={36}
                                      thickness={6}
                                      sx={{ color: 'rgba(145, 158, 171, 0.24)' }}
                                    />
                                    <CircularProgress
                                      variant="determinate"
                                      value={clampedPercent}
                                      size={36}
                                      thickness={6}
                                      sx={{ position: 'absolute', left: 0 }}
                                      color="warning"
                                    />
                                    <Box
                                      sx={{
                                        top: 0,
                                        left: 0,
                                        bottom: 0,
                                        right: 0,
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.primary', fontSize: '0.7rem' }}>
                                        {safePercent.toFixed(0)}%
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              );
                            })()
                          ) : (
                            row[column.key] || '-'
                          )}
                        </TableCell>
                      ))}
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                          <IconButton size="small" color="primary" onClick={() => handleOpenDialog(row)}>
                            <EditIcon size={18} />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => openDeleteDialog(row)}>
                            <DeleteIcon size={18} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{currentRow ? 'Modifica Tipo Allegato' : 'Nuovo Tipo Allegato'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            label="Codice (univoco)"
            value={formData.codice}
            onChange={(event) => setFormData((prev) => ({ ...prev, codice: event.target.value }))}
          />
          <TextField
            margin="dense"
            fullWidth
            label="Nome"
            value={formData.nome}
            onChange={(event) => setFormData((prev) => ({ ...prev, nome: event.target.value }))}
          />
          <TextField
            margin="dense"
            fullWidth
            label="Descrizione"
            value={formData.descrizione}
            onChange={(event) => setFormData((prev) => ({ ...prev, descrizione: event.target.value }))}
            multiline
            minRows={2}
          />
          <TextField
            margin="dense"
            fullWidth
            type="number"
            label="Ordine"
            value={formData.ordine}
            onChange={(event) => setFormData((prev) => ({ ...prev, ordine: event.target.value }))}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <Checkbox
              checked={Boolean(formData.attivo)}
              onChange={(event) => setFormData((prev) => ({ ...prev, attivo: event.target.checked }))}
            />
            <Typography variant="body2">Attivo</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={handleCloseDialog}>
            Annulla
          </Button>
          <Button onClick={handleSubmit} disabled={!String(formData.codice).trim() || !String(formData.nome).trim()}>
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Questa azione è irreversibile.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Stai per eliminare:
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5 }}>
            {deleteTarget?.nome || '-'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Codice: <strong>{deleteTarget?.codice || '-'}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outline" onClick={closeDeleteDialog} disabled={deleteLoading}>
            Annulla
          </Button>
          <Button onClick={handleDeleteConfirm} disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
